/**
 * ⭐⭐ **O TÓPICO QUALIFICAÇÃO DAS PARTES NO DESENHO DE IMÓVEIS — 19/09/2026.**
 *
 * O sexto da fila do pedido dele (PRD §46): quadros no alto, uma parte por vez,
 * os botões, a faixa do que foi pesquisado para a parte, e os blocos da parte —
 * trilha, QUALIFICAÇÃO e CONEXÕES, **cada um com a setinha** (pedido dele,
 * 20/08/2026) e lembrando se ficou aberto, como antes.
 *
 * ⛔ **Muda aparência e lugar, nunca o que o botão faz.** A busca paga
 * (`buscarEmFila`: um por vez, o custo no botão, só o master), acrescentar à
 * qualificação (`FormularioDeDado`), acrescentar vínculo (`FormularioDeVinculo`,
 * só o master), trazer para o caso e apagar vínculo (`TrazerParaOCaso`,
 * `ApagarVinculo`, que perguntam), os atos societários da empresa
 * (`LevarEvolucao`), quem apareceu num documento (`FormularioDeInvestigado`) e o
 * campo de escrita da parte (`Anotacoes`) são as mesmas peças — importadas, não
 * copiadas.
 *
 * ⚠️ **Mudou o LUGAR de três coisas:** a caixa de marcar ao lado de cada nome
 * virou a lista do painel "Buscar"; as duas caixas de acrescentar, que ficavam
 * abertas em todas as partes, abrem pelos botões, para a parte escolhida; e o
 * cadastro de quem apareceu num documento, que ficava no pé, abre pelo botão.
 *
 * ⚠️ **O índice de negociação continua ao lado do nome** (decisão dele,
 * 21/08/2026) — no cabeçalho da parte escolhida.
 */
import { useEffect, useState, type ReactNode } from 'react';
import {
  apiInvestigados,
  apiRelatorios,
  apiSociosNoExterior,
  apiTrilha,
  QUALIFICACOES,
  TIPOS_DE_DADO,
  type ConexaoEncontrada,
  type ConsultaExterna,
  type EvolucaoDaEmpresa,
  type Investigado,
  type LinhaDaTrilha,
} from '../lib/api';
import { usarEhMaster } from '../lib/papel';
import { avisar } from './Avisos';
import { Anotacoes } from './Anotacoes';
import { FormularioDeInvestigado } from './Investigados';
import {
  ApagarVinculo,
  BarraDePorcentagem,
  buscarEmFila,
  CUSTO_POR_INVESTIGADO,
  DA_QUALIFICACAO,
  EM_MONO,
  falhasVigentesDe,
  FormularioDeDado,
  FormularioDeVinculo,
  LevarEvolucao,
  O_QUE_ABRIGA,
  ORDEM,
  ordenarParticipacoes,
  TrazerParaOCaso,
  type CamposDoInvestigado,
} from './PartesInvestigadas';
import { iniciais, Traco } from './ImoveisDoCaso';
import { DiarioDaUniao } from './DiarioDaUniao';
import { DiariosEstaduais } from './DiariosEstaduais';
import { dizer, TrilhaDaApuracao } from './TrilhaDaApuracao';
import '../estilos/imoveis-2026.css';

const emData = (d: string | null | undefined) => (d ? d.split('-').reverse().join('/') : '');
const minuscula = (t: string) => t.charAt(0).toLocaleLowerCase('pt-BR') + t.slice(1);
const PESQUISADA = new Set(['feito', 'nada_consta']);
const dosDados = (i: Investigado) =>
  (i.dados ?? []).filter((d) => DA_QUALIFICACAO.includes(d.tipo) && d.tipo !== 'probabilidade_negociacao');

type Painel = null | 'dado' | 'vinculo' | 'buscar' | 'documento';

export function QualificacaoDoCaso({
  casoId,
  chave,
  anotacoes,
  aoIrAoTopico,
}: {
  casoId: string;
  chave: string;
  anotacoes: CamposDoInvestigado;
  aoIrAoTopico?: (chave: string) => void;
}) {
  const ehMaster = usarEhMaster();
  const [lista, setLista] = useState<Investigado[] | null>(null);
  const [trilha, setTrilha] = useState<Record<string, LinhaDaTrilha[]>>({});
  const [representacoes, setRepresentacoes] = useState<Record<string, string[]>>({});
  const [evolucoes, setEvolucoes] = useState<Record<string, EvolucaoDaEmpresa>>({});
  const [escolhida, setEscolhida] = useState<string | null>(null);
  const [painel, setPainel] = useState<Painel>(null);
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [buscando, setBuscando] = useState<string | null>(null);
  const [consultas, setConsultas] = useState<ConsultaExterna[]>([]);
  const [conexoes, setConexoes] = useState<ConexaoEncontrada[] | null>(null);

  /* O mesmo carregar de antes: a lista, a trilha, a representação e a evolução — cada um cai sozinho. */
  async function carregar() {
    try {
      const i = (await apiInvestigados.listar(casoId)).investigados;
      setLista(i);
      setEscolhida((atual) => (atual && i.some((x) => x.id === atual) ? atual : (emOrdem(i)[0]?.id ?? null)));
    } catch {
      setLista([]);
    }
    try {
      const t = await apiTrilha.doCaso(casoId);
      setTrilha(Object.fromEntries(t.partes.map((p) => [p.investigadoId, p.linhas])));
    } catch {
      setTrilha({});
    }
    try {
      const r = await apiSociosNoExterior.representacoes(casoId);
      setRepresentacoes(Object.fromEntries((r.representacoes?.partes ?? []).map((p) => [p.investigadoId, p.frases])));
    } catch {
      setRepresentacoes({});
    }
    try {
      const e = await apiRelatorios.evolucaoSocietaria(anotacoes.relatorioId);
      setEvolucoes(Object.fromEntries(e.empresas.map((x) => [x.investigadoId, x])));
    } catch {
      setEvolucoes({});
    }
  }

  useEffect(() => {
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [casoId, chave]);

  /* ⚠️ Uma chamada só serve à falha e às conexões — agora só da parte escolhida. */
  async function carregarConsultas(id: string) {
    try {
      const r = await apiInvestigados.consultas(id);
      setConsultas(r.consultas);
      setConexoes(r.conexoes.filter((c) => c.tipoRelacao !== 'Candidato'));
    } catch {
      setConsultas([]);
      setConexoes([]);
    }
  }

  useEffect(() => {
    if (!escolhida) return;
    setConexoes(null);
    void carregarConsultas(escolhida);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [escolhida, buscando]);

  if (lista === null) return <p className="ajuda">Carregando…</p>;
  if (lista.length === 0) {
    return (
      <p className="ajuda ajuda-solta">
        Nenhum investigado cadastrado neste caso ainda. A lista se preenche sozinha a partir da ficha do caso, na aba
        Clientes.
      </p>
    );
  }

  const partes = emOrdem(lista);
  const parte = partes.find((p) => p.id === escolhida) ?? partes[0]!;
  const linhaDe = (id: string, c: string) => (trilha[id] ?? []).find((l) => l.chave === c);
  const semDocumento = lista.filter((i) => !i.documento).length;
  const selecionados = partes.filter((i) => marcados.has(i.id));
  const aoMudar = async () => {
    await carregarConsultas(parte.id);
    await carregar();
  };

  async function buscar() {
    const feitos = await buscarEmFila(
      selecionados.map((i) => i.id),
      setBuscando,
    );
    setMarcados(new Set());
    setPainel(null);
    await carregar();
    if (feitos > 0) avisar(`${feitos} investigado(s) pesquisado(s).`);
  }

  function abrir(p: Painel) {
    setPainel(painel === p ? null : p);
  }

  return (
    <div className="imv">
      <Resumo partes={partes} semDocumento={semDocumento} linhaDe={linhaDe} />

      <div className="imv-pessoas" role="tablist" aria-label="Parte investigada">
        {partes.map((p) => {
          const n = dosDados(p).length;
          return (
            <button
              key={p.id}
              type="button"
              role="tab"
              aria-selected={p.id === parte.id}
              className={p.id === parte.id ? 'ativo' : undefined}
              onClick={() => {
                /* O formulário aberto guarda o que foi digitado, e é de uma parte só. */
                if ((painel === 'dado' || painel === 'vinculo') && p.id !== parte.id) {
                  avisar('Acrescente ou feche o formulário aberto antes de trocar de parte.');
                  return;
                }
                setEscolhida(p.id);
              }}
            >
              <span className="imv-av">{iniciais(p.nome)}</span>
              <span className="imv-txt">
                <b>{p.nome}</b>
                <small>{QUALIFICACOES[p.qualificacao]}</small>
              </span>
              <span
                className={`imv-conta${n === 0 ? ' zero' : ''}`}
                title={n === 0 ? 'nada apurado' : `${n} ${n === 1 ? 'dado' : 'dados'} de qualificação`}
              >
                {n}
              </span>
            </button>
          );
        })}
      </div>

      <div className="imv-acoes">
        <button type="button" className="imv-bt primario" aria-expanded={painel === 'dado'} onClick={() => abrir('dado')}>
          <Traco d={['M12 5v14', 'M5 12h14']} />
          Acrescentar à qualificação
        </button>
        {/* ⚠️ Só master, como antes. */}
        {ehMaster && (
          <button
            type="button"
            className={`imv-bt${painel === 'vinculo' ? ' aberto' : ''}`}
            aria-expanded={painel === 'vinculo'}
            onClick={() => abrir('vinculo')}
          >
            <Traco d={['M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7', 'M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7']} />
            Acrescentar vínculo
          </button>
        )}
        {/* ⚠️ Só master: a busca é paga, e o colaborador não vê valor. */}
        {ehMaster && (
          <button
            type="button"
            className={`imv-bt${painel === 'buscar' ? ' aberto' : ''}`}
            aria-expanded={painel === 'buscar'}
            onClick={() => abrir('buscar')}
          >
            <Traco d={['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16', 'm21 21-4.35-4.35']} />
            Buscar
          </button>
        )}
        <span className="imv-esp" />
        <button
          type="button"
          className={`imv-bt fantasma p${painel === 'documento' ? ' aberto' : ''}`}
          aria-expanded={painel === 'documento'}
          onClick={() => abrir('documento')}
        >
          <Traco d={['M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z', 'M14 3v5h5', 'M12 12v6', 'M9 15h6']} />
          Quem apareceu num documento
        </button>
      </div>

      <div className="imv-fontes">
        <span className="imv-rot">Pesquisado para esta parte</span>
        {(['qualificacao', 'vinculos'] as const).map((c) => {
          const l = linhaDe(parte.id, c);
          return (
            <span key={c} className={`imv-fonte${PESQUISADA.has(l?.palavra ?? '') ? ' ok' : ' falta'}`}>
              <i />
              {c === 'qualificacao' ? 'Qualificação cadastral' : 'Vínculos pessoais'} ·{' '}
              {l ? minuscula(dizer(l)) : 'em branco'}
            </span>
          );
        })}
        {buscando === parte.id && <span className="imv-fonte">Pesquisando esta parte…</span>}
      </div>

      {/* ⭐⭐ **O Diário Oficial da União — 19/09/2026 (PRD §48), onde ele escolheu:**
          o achado pode ser contrato com o governo, cargo, leilão ou sanção, e aqui fica
          junto da pessoa. ⛔ É apontamento: nada entra no caso sozinho. */}
      <DiarioDaUniao key={parte.id} investigado={parte} />

      {/* ⭐ Os diários dos estados — 19/09/2026 (PRD §48.2), logo abaixo do da União:
          a mesma pergunta, feita nos estados que têm porta aberta. */}
      <DiariosEstaduais key={`e-${parte.id}`} investigado={parte} />

      {painel === 'dado' && (
        <div className="imv-painel">
          <FormularioDeDado key={parte.id} investigadoId={parte.id} aoMudar={aoMudar} titulo={`Acrescentar à qualificação · ${parte.nome}`} />
        </div>
      )}
      {painel === 'vinculo' && ehMaster && (
        <div className="imv-painel">
          <FormularioDeVinculo key={parte.id} investigadoId={parte.id} aoMudar={aoMudar} titulo={`Acrescentar vínculo · ${parte.nome}`} />
        </div>
      )}
      {painel === 'buscar' && ehMaster && (
        <div className="imv-painel">
          <p className="imv-painel-titulo">Buscar — marque quem pesquisar</p>
          <p className="ajuda">
            Ficha cadastral, vínculos e pessoas de referência. Cada empresa encontrada custa mais R$ 1,00.{' '}
            <strong>Veículos não entram aqui</strong> — custam R$ 15,41 por CPF e terão seção própria.
          </p>
          <p className="ajuda">
            Empresa investigada ganha também <strong>tipo de sociedade, constituição, situação cadastral, atividades e
            endereço</strong> — de registro público, sem custo.
          </p>
          <ul className="imv-registros">
            {partes.map((i) => (
              <li key={i.id}>
                <label className="imv-registro-nome imv-marcar">
                  <input
                    type="checkbox"
                    checked={marcados.has(i.id)}
                    disabled={buscando !== null}
                    onChange={(e) =>
                      setMarcados((antes) => {
                        const novo = new Set(antes);
                        if (e.target.checked) novo.add(i.id);
                        else novo.delete(i.id);
                        return novo;
                      })
                    }
                  />
                  {i.nome}
                </label>
                <span
                  className={`imv-registro-situacao${buscando === i.id ? ' feito' : i.documentoFormatado ? ' imv-mono' : ''}`}
                >
                  {buscando === i.id ? 'pesquisando…' : (i.documentoFormatado ?? 'sem documento')}
                </span>
              </li>
            ))}
          </ul>
          <div className="imv-acoes">
            <button
              type="button"
              className="imv-bt primario"
              disabled={selecionados.length === 0 || buscando !== null}
              onClick={() => void buscar()}
              title={selecionados.length === 0 ? 'Marque os investigados que quer pesquisar.' : 'Ficha cadastral, vínculos e pessoas de referência'}
            >
              {buscando
                ? 'Pesquisando…'
                : selecionados.length === 0
                  ? 'Marque quem pesquisar'
                  : `Buscar ${selecionados.length} · a partir de R$ ${(selecionados.length * CUSTO_POR_INVESTIGADO)
                      .toFixed(2)
                      .replace('.', ',')}`}
            </button>
          </div>
        </div>
      )}
      {painel === 'documento' && (
        <div className="imv-painel">
          <p className="ajuda">
            Para quem a investigação encontrou lendo um documento — contrato social, certidão, escritura — e{' '}
            <strong>nenhuma consulta traria</strong>, como um menor de idade. O Hub registra que veio da análise
            documental, e não do cliente.
          </p>
          <FormularioDeInvestigado
            casoId={casoId}
            origem="documental"
            aoCancelar={() => setPainel(null)}
            aoSalvar={async () => {
              setPainel(null);
              await carregar();
            }}
          />
        </div>
      )}

      <CabecalhoDaParte parte={parte} />

      {/* ⚠️ A falha fica FORA dos blocos: quem clicou em buscar e não viu nada é quem não vai abrir. */}
      {(() => {
        const falhas = falhasVigentesDe(consultas);
        return (
          falhas.length > 0 && (
            <div className="imv-travas">
              {falhas.map((c) => (
                <p key={c.id}>
                  <strong>{c.funcionalidade}:</strong> {c.falha}
                </p>
              ))}
            </div>
          )
        );
      })()}

      {(trilha[parte.id] ?? []).length > 0 && (
        <BlocoDaParte
          id={`inv-trilha-${parte.id}`}
          titulo="Trilha da apuração"
          subtitulo="O que já se procurou sobre esta parte. A linha leva ao tópico onde se faz."
        >
          <div className="imv-bloco-corpo">
            <TrilhaDaApuracao linhas={trilha[parte.id]!} topicoAtual="investigados" aoIrAoTopico={aoIrAoTopico} />
          </div>
        </BlocoDaParte>
      )}

      <BlocoDaParte
        id={`inv-qualificacao-${parte.id}`}
        titulo="Qualificação"
        subtitulo="O que descreve a pessoa."
        contador={dosDados(parte).length}
      >
        <ListaDaQualificacao parte={parte} />
      </BlocoDaParte>

      {evolucoes[parte.id] && (
        <BlocoDaParte
          id={`inv-atos-${parte.id}`}
          titulo="Atos societários"
          subtitulo="A evolução do quadro de sócios, lida dos contratos sociais e da ficha da junta do caso."
        >
          <div className="imv-bloco-corpo">
            <LevarEvolucao investigado={parte} evolucao={evolucoes[parte.id]!} anotacoes={anotacoes} desenho="imv" />
          </div>
        </BlocoDaParte>
      )}

      <BlocoDaParte
        id={`inv-conexoes-${parte.id}`}
        titulo="Conexões"
        subtitulo="Quem a liga a outrem — parentes, referências e empresas."
        contador={conexoes?.length}
      >
        <ListaDeConexoes parte={parte} conexoes={conexoes} ehMaster={ehMaster} aoMudar={aoMudar} />
        {(representacoes[parte.id] ?? []).length > 0 && (
          <div className="imv-bloco-corpo">
            <div className="imv-interpretacao">
              <p className="imv-rot">Representação de sócio no exterior · vínculo</p>
              {representacoes[parte.id]!.map((f) => (
                <p key={f}>{f}</p>
              ))}
              <p className="ajuda">Sai assim no relatório, no fim deste tópico.</p>
            </div>
          </div>
        )}
      </BlocoDaParte>

      {/* ⭐ O campo de escrita desta pessoa — a mesma peça de antes, com a pessoa carimbada. */}
      <Anotacoes
        relatorioId={anotacoes.relatorioId}
        secaoId={anotacoes.secaoId}
        anotacoes={anotacoes.lista.filter((a) => a.investigadoId === parte.id)}
        travado={anotacoes.travado}
        aoMudar={anotacoes.aoMudar}
        investigadoId={parte.id}
        rotulo={`+ Acrescentar campo de escrita sobre ${parte.nome}`}
      />

      {semDocumento > 0 && (
        <div className="imv-legenda">
          <span className="imv-legenda-longa">
            <span className="imv-info">i</span>
            {semDocumento === 1 ? '1 investigado está sem CPF ou CNPJ.' : `${semDocumento} investigados estão sem CPF ou CNPJ.`}{' '}
            Sem documento a pesquisa não parte sozinha — o casamento só por nome devolve homônimo.
          </span>
        </div>
      )}
    </div>
  );
}

/* A ordem é de responsabilidade, e não alfabética — a mesma de antes (`ORDEM`). */
function emOrdem(lista: Investigado[]): Investigado[] {
  return [...lista].sort((a, b) => ORDEM.indexOf(a.qualificacao) - ORDEM.indexOf(b.qualificacao));
}

/* ── Os quadros ───────────────────────────────────────────────────────────── */
function Resumo({
  partes,
  semDocumento,
  linhaDe,
}: {
  partes: Investigado[];
  semDocumento: number;
  linhaDe: (id: string, c: string) => LinhaDaTrilha | undefined;
}) {
  const doCliente = partes.filter((p) => p.origemNoCaso === 'cliente').length;
  const pesquisadas = (c: string) => partes.filter((p) => PESQUISADA.has(linhaDe(p.id, c)?.palavra ?? '')).length;
  return (
    <div className="imv-resumo">
      <div className="imv-kpi k-fora">
        <span className="imv-rot">Partes</span>
        <strong>{partes.length}</strong>
        <small>
          {doCliente} do cliente{partes.length > doCliente ? ` · ${partes.length - doCliente} da investigação` : ''}
        </small>
      </div>
      <div className="imv-kpi k-sem">
        <span className="imv-rot">Sem documento</span>
        <strong>{semDocumento}</strong>
        <small>{semDocumento === 0 ? 'todas com CPF ou CNPJ' : 'a pesquisa não parte sozinha'}</small>
      </div>
      <div className="imv-kpi k-livre">
        <span className="imv-rot">Qualificação</span>
        <strong>
          {pesquisadas('qualificacao')} de {partes.length}
        </strong>
        <small>com resposta da consulta</small>
      </div>
      <div className="imv-kpi k-livre">
        <span className="imv-rot">Vínculos</span>
        <strong>
          {pesquisadas('vinculos')} de {partes.length}
        </strong>
        <small>com resposta da consulta</small>
      </div>
    </div>
  );
}

/* ── O cabeçalho da parte: o nome, o documento e o índice ao lado ──────────── */
function CabecalhoDaParte({ parte }: { parte: Investigado }) {
  /* ⚠️ Vale o mais recente — duas consultas deixam duas linhas, e o cabeçalho mostra uma. */
  const indice = [...(parte.dados ?? [])]
    .filter((d) => d.tipo === 'probabilidade_negociacao')
    .sort((a, b) => ((a.coletadoEm ?? '') < (b.coletadoEm ?? '') ? 1 : -1))[0];
  return (
    <div className="imv-parte">
      <div className="imv-parte-id">
        <b>{parte.nome}</b>
        <small>
          {parte.tipo === 'pj' ? 'pessoa jurídica' : 'pessoa física'} · {QUALIFICACOES[parte.qualificacao]} —{' '}
          {O_QUE_ABRIGA[parte.qualificacao]}
        </small>
      </div>
      {indice && <BarraDePorcentagem valor={indice.valor} faixa={indice.nota} />}
      <span className={`imv-mono imv-parte-doc${parte.documentoFormatado ? '' : ' sem'}`}>
        {parte.documentoFormatado ?? 'sem documento'}
      </span>
    </div>
  );
}

/* ── O bloco com a setinha — lembra se ficou aberto, na mesma chave de antes ── */
function BlocoDaParte({
  id,
  titulo,
  subtitulo,
  contador,
  children,
}: {
  id: string;
  titulo: string;
  subtitulo: string;
  contador?: number;
  children: ReactNode;
}) {
  const chave = `kairos-secao-${id}`;
  const [aberto, setAberto] = useState<boolean>(() => {
    try {
      return localStorage.getItem(chave) !== 'fechada';
    } catch {
      return true;
    }
  });
  useEffect(() => {
    try {
      setAberto(localStorage.getItem(chave) !== 'fechada');
    } catch {
      setAberto(true);
    }
  }, [chave]);
  function alternar() {
    const novo = !aberto;
    setAberto(novo);
    try {
      localStorage.setItem(chave, novo ? 'aberta' : 'fechada');
    } catch {
      /* preferência de tela: sem o navegador guardar, só não se lembra */
    }
  }
  return (
    <section className={`imv-lista imv-bloco-parte${aberto ? '' : ' fechado'}`}>
      <button type="button" className="imv-bloco-cab" onClick={alternar} aria-expanded={aberto} aria-controls={`bloco-${id}`}>
        <span className="imv-bloco-titulo">
          <span className="imv-rot">{titulo}</span>
          {contador !== undefined && contador > 0 && <span className="imv-conta">{contador}</span>}
        </span>
        <small>{subtitulo}</small>
        <span className="imv-chev" aria-hidden="true">
          <Traco d={[aberto ? 'm6 15 6-6 6 6' : 'm6 9 6 6 6-6']} />
        </span>
      </button>
      <div id={`bloco-${id}`} hidden={!aberto}>
        {children}
      </div>
    </section>
  );
}

/* ── A QUALIFICAÇÃO: o que descreve a pessoa ──────────────────────────────── */
function ListaDaQualificacao({ parte }: { parte: Investigado }) {
  const dados = dosDados(parte);
  const fixos: Array<[string, string | null, boolean]> = [
    [parte.tipo === 'pj' ? 'Abertura' : 'Nascimento', emData(parte.nascimentoEm) || null, true],
    ['Mãe', parte.nomeMae, false],
    ['Pai', parte.nomePai, false],
  ];
  const linhas: Array<{ chave: string; rotulo: string; valor: string; mono: boolean; nota?: string | null; deConsulta?: boolean; primeira: boolean }> =
    [];
  for (const [rotulo, v, mono] of fixos) if (v) linhas.push({ chave: rotulo, rotulo, valor: v, mono, primeira: true });
  for (const t of DA_QUALIFICACAO) {
    /* ⚠️ A lista mostra TUDO, sem agrupar — decisão dele, 21/08/2026: uma linha por estabelecimento. */
    const doTipo = t === 'participacao_societaria' ? ordenarParticipacoes(dados.filter((d) => d.tipo === t)) : dados.filter((d) => d.tipo === t);
    doTipo.forEach((d, n) =>
      linhas.push({
        chave: d.id,
        rotulo: TIPOS_DE_DADO[t],
        valor: d.valor,
        mono: EM_MONO.includes(t),
        nota: d.nota,
        /* ⚠️ A origem aparece AQUI e em lugar nenhum do documento entregue. */
        deConsulta: !!d.origem,
        primeira: n === 0,
      }),
    );
  }
  return (
    <>
      <div className="imv-grade imv-grade-q imv-cab">
        <span className="imv-rot">Dado</span>
        <span className="imv-rot">Valor</span>
        <span className="imv-rot">Nota</span>
        <span />
      </div>
      {linhas.length === 0 ? (
        <p className="imv-vazio">Nada apurado ainda. Use Buscar, acima, ou acrescente à mão.</p>
      ) : (
        linhas.map((l) => (
          <div key={l.chave} className={`imv-grade imv-grade-q imv-q-linha${l.primeira ? ' primeira' : ''}`}>
            <span className="imv-q-rotulo">{l.primeira ? l.rotulo : ''}</span>
            <span className={`imv-q-valor${l.mono ? ' imv-mono' : ''}`}>{l.valor}</span>
            <span className="imv-q-nota">{l.nota ?? ''}</span>
            <span>{l.deConsulta && <span className="imv-tag cinza">de consulta</span>}</span>
          </div>
        ))
      )}
    </>
  );
}

/* ── As CONEXÕES: quem liga a parte a outrem ──────────────────────────────── */
function ListaDeConexoes({
  parte,
  conexoes,
  ehMaster,
  aoMudar,
}: {
  parte: Investigado;
  conexoes: ConexaoEncontrada[] | null;
  ehMaster: boolean;
  aoMudar: () => Promise<void>;
}) {
  const [aTrazer, setATrazer] = useState<{ id: string; nome: string } | null>(null);
  const [aApagar, setAApagar] = useState<{ id: string; nome: string } | null>(null);
  return (
    <>
      <div className="imv-grade imv-grade-c imv-cab">
        <span className="imv-rot">Vínculo</span>
        <span className="imv-rot">Pessoa ou empresa</span>
        <span className="imv-rot">Origem</span>
        <span />
      </div>
      {conexoes === null ? (
        <p className="imv-vazio">Carregando…</p>
      ) : conexoes.length === 0 ? (
        <p className="imv-vazio">
          Nenhum vínculo apurado. Use Buscar, acima — ou acrescente à mão o que você achou num documento.
        </p>
      ) : (
        conexoes.map((c) => (
          <div key={c.id} className="imv-grade imv-grade-c imv-q-linha primeira">
            <span className="imv-sit-coluna">
              <span className="imv-sit terceiro">
                <i />
                {c.relacao}
              </span>
            </span>
            <span className="imv-bem">
              <b>{c.nome}</b>
              {c.documento && <span className="imv-mono">{c.documento}</span>}
            </span>
            {/* ⚠️ "Apurado em documento" aparece AQUI e em lugar nenhum do documento entregue. */}
            <span className="imv-bem">
              {c.aMao ? (
                <>
                  <span className="imv-tag">apurado em documento</span>
                  {c.detalhe && <small>{c.detalhe}</small>}
                </>
              ) : (
                <span className="imv-tag cinza">de consulta</span>
              )}
            </span>
            <span className="imv-q-acoes">
              {c.virouInvestigadoId ? (
                <span className="imv-tag cinza">já está no caso</span>
              ) : (
                ehMaster && (
                  <>
                    <button type="button" className="imv-bt p" onClick={() => setATrazer({ id: c.id, nome: c.nome })}>
                      Trazer para o caso
                    </button>
                    {/* ⛔ Só o que foi digitado tem como sumir. */}
                    {c.aMao && (
                      <button type="button" className="imv-bt p fantasma perigo" onClick={() => setAApagar({ id: c.id, nome: c.nome })}>
                        Apagar
                      </button>
                    )}
                  </>
                )
              )}
            </span>
          </div>
        ))
      )}
      {aTrazer && <TrazerParaOCaso investigadoId={parte.id} conexao={aTrazer} aoFechar={() => setATrazer(null)} aoMudar={aoMudar} />}
      {aApagar && <ApagarVinculo investigadoId={parte.id} conexao={aApagar} aoFechar={() => setAApagar(null)} aoMudar={aoMudar} />}
    </>
  );
}
